import { DatePickerButton } from "@/components/DatePickerButton";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plug, Download, CheckCircle2, XCircle } from "lucide-react";
import { useApi, errMsg, ago, nextSaturday, isStalled } from "@/lib/api";

const pollJob = async (api, id) => {
  for (let i = 0; i < 90; i++) {
    const j = await api("get", `/admin/jobs/${id}`);
    if (j.state !== "generating" || isStalled(j)) return j;
    await new Promise((r) => setTimeout(r, 3000));
  }
  throw new Error("Import is taking too long. Check the log below shortly.");
};

const RunLog = ({ runs }) => (
  <div data-testid="ff-run-log">
    <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Recent imports</p>
    {runs.length === 0 && <p className="text-xs text-slate-500">None yet. Meetings import automatically at 4am Brisbane time and refresh every hour from 6am to 4pm Brisbane time.</p>}
    {runs.map((r) => (
      <p key={r.id} className="text-xs text-slate-400 py-1 border-t border-white/5">
        <span className={r.state === "error" || isStalled(r) ? "text-rose-300" : r.state === "done" ? "text-emerald-300" : "text-amber-300"}>{isStalled(r) ? "stalled" : r.state}</span>
        {" · "}{r.trigger === "cron" ? "5am import" : r.trigger === "cron-refresh" ? "hourly refresh" : "manual"} · {r.date?.split("-").reverse().join("/")}{r.slug ? ` · ${r.slug}` : " · all metro"} · {ago(r.created_at)}
        {isStalled(r) && <span className="block text-rose-300">No progress for over 15 minutes. It likely stopped when the server restarted. Run the import again.</span>}
        {r.error && <span className="block text-rose-300">{r.error}</span>}
        {r.results?.flatMap((x) => x.errors || []).slice(0, 3).map((e) => <span key={e} className="block text-rose-300">{e}</span>)}
      </p>
    ))}
  </div>
);

export const LiveDataPanel = ({ onImported }) => {
  const api = useApi();
  const [date, setDate] = useState(nextSaturday());
  const [all, setAll] = useState(false);
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState("");
  const [runs, setRuns] = useState([]);
  const loadRuns = useCallback(() => api("get", "/admin/formfav/runs").then((d) => { setRuns(d.runs); if (d.last_test) setTest(d.last_test); }).catch(() => {}), [api]);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const runTest = async () => {
    setBusy("test");
    try { const t = await api("get", "/admin/formfav/test"); setTest(t); t.ok ? toast.success("PuntersEdge connected") : toast.error(t.error); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const load = async () => {
    setBusy("load"); setErr("");
    try { setList(await api("get", `/admin/formfav/meetings?date=${date}&all=${all}`)); }
    catch (e) { setList(null); setErr(errMsg(e)); }
    setBusy("");
  };
  const doImport = async (slug) => {
    setBusy(slug || "all");
    try {
      const j = await pollJob(api, (await api("post", "/admin/formfav/import", { date, slug })).id);
      if (j.state === "error") throw new Error(j.error);
      const errs = j.results.flatMap((r) => r.errors);
      errs.length ? toast.warning(`Imported with ${errs.length} problem(s): ${errs[0]}`) : toast.success(`Imported ${j.results.length} meeting(s)`);
      await load(); onImported();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(""); loadRuns();
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-emerald-500/30 p-5 space-y-4" data-testid="ff-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-lg font-bold mr-auto">Race fields & form · PuntersEdge</h2>
        <button data-testid="ff-test-button" onClick={runTest} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm">
          <Plug className="w-4 h-4" />{busy === "test" ? "Testing…" : "Test connection"}
        </button>
      </div>
      {test && (
        <p data-testid="ff-test-result" className={`flex items-center gap-2 text-sm ${test.ok ? "text-emerald-300" : "text-rose-300"}`}>
          {test.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {test.ok ? `Connected — ${test.au_meetings} Australian meetings found for ${test.date.split("-").reverse().join("/")} (${test.ms} ms)` : `Failed — ${test.error}`}
          <span className="text-slate-500 text-xs">· tested {ago(test.at)}</span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <DatePickerButton testid="ff-date-input" value={date} onChange={setDate} className="bg-[#0B101D] border border-white/10 rounded-full px-3 py-2 text-sm text-slate-100 hover:border-blue-500/50 transition-colors" />
        <label className="flex items-center gap-1.5 text-xs text-slate-400"><input data-testid="ff-all-toggle" type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />Include country meetings</label>
        <button data-testid="ff-load-button" onClick={load} disabled={!!busy} className="px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-sm font-semibold disabled:opacity-50">{busy === "load" ? "Loading…" : "Load meetings"}</button>
        {list && !all && list.meetings.length > 0 && (
          <button data-testid="ff-import-all" onClick={() => doImport(null)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold disabled:opacity-50">
            <Download className="w-4 h-4" />{busy === "all" ? "Importing all…" : "Import all metro"}
          </button>
        )}
      </div>
      {err && <p className="text-sm text-rose-300" data-testid="ff-error">{err}</p>}
      {list && (
        <div data-testid="ff-meetings-list">
          <p className="text-[11px] text-slate-500 mb-1">Retrieved from PuntersEdge {ago(list.retrieved_at)}</p>
          {list.meetings.length === 0 && <p className="text-sm text-slate-400" data-testid="ff-no-meetings">PuntersEdge has no matching meetings for {list.date?.split("-").reverse().join("/")}.</p>}
          {list.meetings.map((m) => (
            <div key={m.slug} className="flex flex-wrap items-center gap-2 py-2 border-t border-white/5 text-sm" data-testid={`ff-meeting-${m.slug}`}>
              <span className="font-bold">{m.track}</span><span className="text-xs text-slate-500">{m.state || "AU"} · {m.races.length} races{m.abandoned ? " · ABANDONED" : ""}</span>
              <span className="text-xs text-slate-500 mr-auto">{m.fetched_at ? <>saved {ago(m.fetched_at)} · <Link className="text-blue-400" to={`/meeting/${m.meeting_id}`}>open</Link></> : "not imported"}</span>
              <button data-testid={`ff-import-${m.slug}`} onClick={() => doImport(m.slug)} disabled={!!busy || m.abandoned} className="px-3 py-1 rounded-full border border-white/10 hover:bg-white/5 text-xs disabled:opacity-40">
                {busy === m.slug ? "Importing…" : m.fetched_at ? "Re-import" : "Import"}
              </button>
            </div>
          ))}
        </div>
      )}
      <RunLog runs={runs} />
    </section>
  );
};
